type GetSellarCreditLowBalanceInfoParameters = {
  creditLimit: number;
  availableBalance: number;
};

type Result<T> = { error: null; data: T } | { error: Error; data: null };

const MAX_THRESHOLD = 100_000; // £1000

/**
 * Determines whether or not a Sellar Credit (SC) Balance is low.
 * A Sellar Credit Balance is considered low when it is no more than 20% of
 * the credit limit, capped at £1000 (100_000 in pence)
 * @param creditLimit - Total credit limit in pence
 * @param availableBalance - Current available balance in pence
 */
export const getSellarCreditLowBalanceInfo = ({
  creditLimit,
  availableBalance,
}: GetSellarCreditLowBalanceInfoParameters): Result<{
  isLow: boolean;
  lowBalanceThreshold: number;
}> => {
  if (!Number.isFinite(availableBalance)) {
    return {
      error: new Error("availableBalance must be a finite number"),
      data: null,
    };
  }

  if (!Number.isFinite(creditLimit)) {
    return {
      error: new Error("creditLimit must be a finite number"),
      data: null,
    };
  }

  if (availableBalance < 0) {
    return {
      error: new Error("availableBalance cannot be less than zero"),
      data: null,
    };
  }

  if (creditLimit < 0) {
    return {
      error: new Error("creditLimit cannot be less than zero"),
      data: null,
    };
  }

  // eslint-disable-next-line sellar/use-common-pricing -- Low-balance alerts use 20% of the credit limit and preserve fractional precision.
  const threshold = Math.min(creditLimit * 0.2, MAX_THRESHOLD);

  return {
    error: null,
    data: {
      isLow: availableBalance <= threshold,
      lowBalanceThreshold: threshold,
    },
  };
};
